/* eslint-disable no-console */
// src/hooks/orderbooks/useLbankSpot.js
//
// LBank SPOT orderbook (REST snapshot + WS depth)
// - REST идёт через локальный прокси (по умолчанию http://localhost:8080/proxy)
// - Домен переключаем между рекомендованными: lbkex.net / api.lbkex.com / api.lbank.info
// - Символы нормализуются к виду "eth_usdt"
// - Возвращает { isConnected, error, orderBook:{asks,bids}, tickerData:{lastPrice}? }

import { useEffect, useRef, useState } from "react";

const L = (...a) => console.log("[LBank SPOT]", ...a);

// ======= Константы =======
const DEFAULT_PROXY = `${process.env.REACT_APP_BACKEND_URL}/proxy`;

// Рекомендованные публичные домены из доки (public / без подписи)
const REST_HOSTS = [
  "https://www.lbkex.net/v2/depth.do",  // основной по доке
  "https://api.lbkex.com/v2/depth.do",
  "https://api.lbank.info/v2/depth.do",
];
// WS (официальный V2 поток маркет-данных):
const WS_URL = "wss://www.lbkex.net/ws/V2/";

// ======= Вспомогательные =======
function withProxy(url, proxyBase = DEFAULT_PROXY) {
  const clean = url.replace(/^https?:\/\//, "");
  return `${proxyBase}/https://${clean}`;
}

// вход может прийти в разных форматах: "ETHUSDT" | "ETH_USDT" | "eth_usdt"
// для REST/WS LBank SPOT нужен нижний регистр и подчёркивание: "eth_usdt"
function toLbankSpotPair(sym) {
  if (!sym) return "";
  const s = String(sym).trim();
  if (!s) return "";
  const upper = s.toUpperCase();
  // "ETH_USDT"
  if (upper.includes("_")) {
    const [b, q] = upper.split("_");
    return `${(b || "").trim()}_${(q || "USDT").trim()}`.toLowerCase();
  }
  // "ETHUSDT"
  if (upper.endsWith("USDT")) {
    const base = upper.slice(0, -4);
    return `${base}_USDT`.toLowerCase();
  }
  // "ETH-USDT"
  if (upper.includes("-")) {
    const [b, q] = upper.split("-");
    return `${(b || "").trim()}_${(q || "USDT").trim()}`.toLowerCase();
  }
  // fallback: просто склеим с USDT
  return `${upper}_USDT`.toLowerCase();
}

// Нормализация стакана LBank SPOT
function normalizeDepth(asksRaw, bidsRaw) {
  // Ожидаем массивы вида [[price, qty], ...]
  const asks = Array.isArray(asksRaw)
    ? asksRaw
        .map(([p, q]) => [Number(p), Number(q)])
        .filter(([p, q]) => Number.isFinite(p) && Number.isFinite(q))
        .sort((a, b) => a[0] - b[0]) // asks — по возрастанию
    : [];

  const bids = Array.isArray(bidsRaw)
    ? bidsRaw
        .map(([p, q]) => [Number(p), Number(q)])
        .filter(([p, q]) => Number.isFinite(p) && Number.isFinite(q))
        .sort((a, b) => b[0] - a[0]) // bids — по убыванию
    : [];

  return { asks, bids };
}

// ======= REST снапшот с бэкап-хостами =======
async function fetchSpotDepthSnapshot({
  symbol,
  size = 100,
  proxyBase = DEFAULT_PROXY,
  timeoutMs = 8000,
} = {}) {
  const pair = toLbankSpotPair(symbol);
  if (!pair) throw new Error("Empty pair for LBank SPOT");

  const qs = `symbol=${encodeURIComponent(pair)}&size=${size}&_ts=${Date.now()}`;

  // Заголовки: в доке советуют x-www-form-urlencoded; для GET — необязательно,
  // но некоторые edge-узлы 403'ят без content-type/accept.
  const headers = {
    accept: "application/json, text/plain, */*",
    "content-type": "application/x-www-form-urlencoded",
  };

  let lastErr;
  for (const host of REST_HOSTS) {
    const url = withProxy(`${host}?${qs}`, proxyBase);
    try {
      const ctrl = new AbortController();
      const to = setTimeout(() => ctrl.abort("timeout"), timeoutMs);

      const res = await fetch(url, { method: "GET", headers, signal: ctrl.signal, headers: { 'ngrok-skip-browser-warning': '1' } });
      clearTimeout(to);

      if (!res.ok) {
        lastErr = new Error(`HTTP ${res.status} on ${host}`);
        L("REST fail", { host, status: res.status });
        continue; // пробуем следующий хост
      }

      // Ответ может быть либо {asks,bids}, либо {data:{asks,bids}}, либо {result:true, data:{...}}
      const json = await res.json().catch(() => ({}));
      const dataNode = json?.data ?? json;

      const asks = dataNode?.asks ?? [];
      const bids = dataNode?.bids ?? [];

      const depth = normalizeDepth(asks, bids);
      // sanity-check
      if (depth.asks.length === 0 && depth.bids.length === 0) {
        lastErr = new Error(`Empty orderbook from ${host}`);
        L("REST empty", { host });
        continue;
      }

      L("REST ok", { host, pair, asks: depth.asks.length, bids: depth.bids.length });
      return depth;
    } catch (e) {
      lastErr = e;
      L("REST error", { host, msg: e?.message || e });
      continue; // следующий хост
    }
  }

  throw lastErr || new Error("All LBank SPOT hosts failed");
}

// ======= WS подписка =======
function openSpotDepthWS({ symbol, depth = 100, onDepth }) {
  const pair = toLbankSpotPair(symbol);
  if (!pair) return () => {};

  const ws = new WebSocket(WS_URL);
  let subscribed = false;
  let alive = true;

  const send = (obj) => {
    try {
      ws.send(JSON.stringify(obj));
    } catch (e) {
      console.warn("[LBank SPOT] ws send error", e?.message || e);
    }
  };

  const close = () => {
    alive = false;
    try {
      if (subscribed && ws.readyState === WebSocket.OPEN) {
        send({ action: "unsubscribe", subscribe: "depth", depth: String(depth), pair });
      }
    } catch {}
    try { ws.close(); } catch {}
  };

  ws.onopen = () => {
    if (!alive) return;
    subscribed = true;
    send({ action: "subscribe", subscribe: "depth", depth: String(depth), pair });
    L("WS subscribed", { pair, depth });
  };

  ws.onmessage = (evt) => {
    try {
      const msg = JSON.parse(evt.data);
      if (msg?.type !== "depth") return;
      // формата:
      // { type:"depth", pair:"eth_usdt", count:100, depth:{ asks:[[p,q]...], bids:[[p,q]...] } }
      const d = msg.depth || {};
      const { asks, bids } = normalizeDepth(d.asks, d.bids);
      onDepth?.({ asks, bids });
    } catch (e) {
      console.warn("[LBank SPOT] WS parse error", e?.message || e);
    }
  };

  ws.onerror = (e) => console.warn("[LBank SPOT] WS error", e);
  ws.onclose = () => L("WS closed");

  return close;
}

// ======= Хук =======
/**
 * useLbankSpot(symbol)
 * symbol может быть "ETHUSDT", "ETH_USDT" — хук сам приведёт к "eth_usdt".
 * Возвращает:
 * - isConnected (bool)
 * - error (string|null)
 * - orderBook { asks:[[price,qty]...], bids:[[price,qty]...] }
 * - tickerData { lastPrice }: берём mid-price для совместимости с UI
 */
export function useLbankSpot(symbol) {
  const [orderBook, setOrderBook] = useState({ asks: [], bids: [] });
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const closeRef = useRef(null);

  useEffect(() => {
    // защита от пустых/мусорных символов
    const pair = toLbankSpotPair(symbol);
    if (!pair) {
      L("skip: empty symbol", { symbol });
      setOrderBook({ asks: [], bids: [] });
      setIsConnected(false);
      setError("Empty symbol");
      return () => {};
    }

    let cancelled = false;

    const run = async () => {
      setError(null);
      setIsConnected(false);
      setOrderBook({ asks: [], bids: [] });

      try {
        // 1) загрузим REST снапшот (с бэкап-хостами)
        const snap = await fetchSpotDepthSnapshot({
          symbol: pair,
          size: 100,
          proxyBase: DEFAULT_PROXY,
        });
        if (cancelled) return;
        setOrderBook(snap);

        // 2) подключим WS
        const closer = openSpotDepthWS({
          symbol: pair,
          depth: 100,
          onDepth: (d) => {
            if (cancelled) return;
            setOrderBook(d);
            setIsConnected(true);
          },
        });
        closeRef.current = closer;
      } catch (e) {
        if (!cancelled) {
          console.warn("[LBank SPOT] init error", e?.message || e);
          setError(e?.message || String(e));
          // fallback: пометим как не подключен, но UI продолжит работать
          setIsConnected(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
      try { closeRef.current?.(); } catch {}
      closeRef.current = null;
      setIsConnected(false);
    };
  }, [symbol]);

  // простой lastPrice (mid)
  const bestAsk = orderBook.asks?.[0]?.[0];
  const bestBid = orderBook.bids?.[0]?.[0];
  const lastPrice =
    Number.isFinite(bestAsk) && Number.isFinite(bestBid)
      ? (Number(bestAsk) + Number(bestBid)) / 2
      : undefined;

  return {
    isConnected,
    error,
    orderBook,
    tickerData: Number.isFinite(lastPrice) ? { lastPrice } : null,
  };
}

export default useLbankSpot;
